import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import {Users, DollarSign, Link2, TrendingUp, LogOut, Copy, Check, Settings, BarChart3, MessageSquare} from 'lucide-react'
import DashboardSwitcher from "../components/DashboardSwitcher"

type Tab = "overview" | "referrals" | "commissions" | "settings"

interface AffiliateProfile {
  referral_code?: string
  total_referrals?: number
  active_referrals?: number
  total_commission?: number
  conversion_rate?: number
}

interface Referral {
  _id: string
  name: string
  email: string
  status: string
  date_joined: string
  commission_earned?: number
}

export default function AffiliateDashboard() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>("overview")
  const [profile, setProfile] = useState<AffiliateProfile | null>(null)
  const [referrals, setReferrals] = useState<Referral[]>([])
  const [loading, setLoading] = useState(true)
  const [copied, setCopied] = useState(false)

  const referralUrl = `https://nursinghive.com/signup?ref=${profile?.referral_code || 'LOADING'}`

  useEffect(() => {
    loadProfile()
    if (activeTab === "referrals") {
      loadReferrals()
    }
    if (activeTab === "messages") {
      navigate("/dashboard/affiliate/messages")
    }
  }, [activeTab, navigate])

  const loadProfile = async () => {
    try {
      const userId = lumi.auth.user?.userId
      if (!userId) return

      const result = await lumi.entities.UserProfiles.list({
        filter: { user_id: userId }
      })

      if (result.list.length > 0) {
        const profileData = result.list[0] as any
        setProfile({
          referral_code: profileData.referral_code || userId.slice(-8).toUpperCase(),
          total_referrals: profileData.total_referrals || 0,
          active_referrals: profileData.active_referrals || 0,
          total_commission: profileData.total_commission || 0,
          conversion_rate: profileData.conversion_rate || 0
        })
      }
    } catch (error) {
      console.error("Failed to load profile:", error)
    } finally {
      setLoading(false)
    }
  }

  const loadReferrals = async () => {
    try {
      // Mock data - replace with actual API call
      setReferrals([])
    } catch (error) {
      console.error("Failed to load referrals:", error)
    }
  }

  const handleSignOut = () => {
    lumi.auth.signOut()
    toast.success("Signed out successfully")
    navigate("/")
  }

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralUrl)
    setCopied(true)
    toast.success("Referral link copied!")
    setTimeout(() => setCopied(false), 2000)
  }

  const renderOverview = () => (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome, {lumi.auth.user?.userName}!</h2>
        <p className="text-gray-600">
          Track your referrals, commissions, and performance here.
        </p>
      </div>

      {/* Referral Link Card */}
      <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 mb-6 text-white">
        <div className="flex items-center gap-2 mb-3">
          <Link2 className="w-5 h-5" />
          <h3 className="font-semibold text-lg">Your Referral Link</h3>
        </div>
        <div className="bg-white/10 backdrop-blur rounded-lg p-4 mb-4">
          <p className="text-sm font-mono break-all">{referralUrl}</p>
        </div>
        <button
          onClick={copyReferralLink}
          className="w-full sm:w-auto px-6 py-2 bg-white text-purple-600 rounded-lg font-medium hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4" />
              Copied!
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copy Link
            </>
          )}
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <Users className="w-8 h-8 text-teal-600" />
            <span className="text-2xl font-bold text-gray-900">{profile?.total_referrals || 0}</span>
          </div>
          <p className="text-gray-600">Total Referrals</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <Users className="w-8 h-8 text-green-600" />
            <span className="text-2xl font-bold text-gray-900">{profile?.active_referrals || 0}</span>
          </div>
          <p className="text-gray-600">Active Nurses</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <DollarSign className="w-8 h-8 text-green-600" />
            <span className="text-2xl font-bold text-gray-900">${profile?.total_commission || 0}</span>
          </div>
          <p className="text-gray-600">Total Earnings</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <TrendingUp className="w-8 h-8 text-purple-600" />
            <span className="text-2xl font-bold text-gray-900">{profile?.conversion_rate || 0}%</span>
          </div>
          <p className="text-gray-600">Conversion Rate</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm p-8">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <button
            onClick={() => setActiveTab("referrals")}
            className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Users className="w-6 h-6 text-teal-600" />
            <div className="text-left">
              <div className="font-semibold text-gray-900">View Referrals</div>
              <div className="text-sm text-gray-600">See all your referred nurses</div>
            </div>
          </button>
          <button
            onClick={() => setActiveTab("commissions")}
            className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <DollarSign className="w-6 h-6 text-green-600" />
            <div className="text-left">
              <div className="font-semibold text-gray-900">Commission Details</div>
              <div className="text-sm text-gray-600">Track your earnings</div>
            </div>
          </button>
        </div>
      </div>

      {/* How It Works */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="font-semibold text-blue-900 mb-3">How Affiliate Program Works</h3>
        <ol className="space-y-2 text-blue-800 text-sm">
          <li className="flex gap-2">
            <span className="font-semibold">1.</span>
            <span>Share your unique referral link with nurses</span>
          </li>
          <li className="flex gap-2">
            <span className="font-semibold">2.</span>
            <span>When they sign up and get verified, you earn commission</span>
          </li>
          <li className="flex gap-2">
            <span className="font-semibold">3.</span>
            <span>Track all referrals and earnings in your dashboard</span>
          </li>
          <li className="flex gap-2">
            <span className="font-semibold">4.</span>
            <span>Get paid monthly via your preferred payment method</span>
          </li>
        </ol>
      </div>
    </div>
  )

  const renderReferrals = () => (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Referrals</h2>

      {referrals.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Referrals Yet</h3>
          <p className="text-gray-600 mb-6">
            Start sharing your referral link to earn commissions
          </p>
          <button
            onClick={copyReferralLink}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors inline-flex items-center gap-2"
          >
            <Copy className="w-4 h-4" />
            Copy Referral Link
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Joined</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {referrals.map((referral) => (
                  <tr key={referral._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{referral.name}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{referral.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        referral.status === "Active" ? "bg-green-100 text-green-800" :
                        referral.status === "Pending" ? "bg-yellow-100 text-yellow-800" :
                        "bg-gray-100 text-gray-800"
                      }`}>
                        {referral.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(referral.date_joined).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${referral.commission_earned || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )

  const renderCommissions = () => (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Commission Details</h2>

      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-600 mb-1">Total Earnings</div>
          <div className="text-3xl font-bold text-gray-900">${profile?.total_commission || 0}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-600 mb-1">Pending Payout</div>
          <div className="text-3xl font-bold text-yellow-600">$0</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm text-gray-600 mb-1">Paid Out</div>
          <div className="text-3xl font-bold text-green-600">$0</div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-8 text-center">
        <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">Commission History</h3>
        <p className="text-gray-600 mb-6">
          Detailed commission tracking and payout history
        </p>
        <div className="inline-block px-4 py-2 bg-purple-100 text-purple-800 rounded-lg font-medium">
          Coming Soon
        </div>
      </div>
    </div>
  )

  const renderSettings = () => (
    <div className="bg-white rounded-lg shadow-sm p-8 text-center">
      <Settings className="w-16 h-16 text-gray-300 mx-auto mb-4" />
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Settings</h2>
      <p className="text-gray-600 mb-6">
        Manage payment methods, notifications, and account preferences
      </p>
      <div className="inline-block px-4 py-2 bg-gray-100 text-gray-800 rounded-lg font-medium">
        Coming Soon
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://i.imghippo.com/files/KBd1355nw.png"
              alt="Nursing Hive"
              className="w-10 h-auto"
            />
            <h1 className="text-xl font-bold text-gray-900">Affiliate Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <DashboardSwitcher />
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <nav className="bg-white rounded-lg shadow-sm p-4 space-y-1">
              <button
                onClick={() => setActiveTab("overview")}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === "overview" ? "bg-purple-50 text-purple-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <BarChart3 className="w-5 h-5" />
                <span className="font-medium">Overview</span>
              </button>
              <button
                onClick={() => setActiveTab("referrals")}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === "referrals" ? "bg-purple-50 text-purple-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Users className="w-5 h-5" />
                <span className="font-medium">Referrals</span>
              </button>
              <button
                onClick={() => setActiveTab("commissions")}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === "commissions" ? "bg-purple-50 text-purple-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <DollarSign className="w-5 h-5" />
                <span className="font-medium">Commissions</span>
              </button>
              <button
                onClick={() => setActiveTab("messages")}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === "messages" ? "bg-purple-50 text-purple-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <MessageSquare className="w-5 h-5" />
                <span className="font-medium">Messages</span>
              </button>
              <button
                onClick={() => setActiveTab("settings")}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  activeTab === "settings" ? "bg-purple-50 text-purple-700" : "text-gray-700 hover:bg-gray-50"
                }`}
              >
                <Settings className="w-5 h-5" />
                <span className="font-medium">Settings</span>
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {activeTab === "overview" && renderOverview()}
            {activeTab === "referrals" && renderReferrals()}
            {activeTab === "commissions" && renderCommissions()}
            {activeTab === "settings" && renderSettings()}
          </div>
        </div>
      </div>
    </div>
  )
}
